import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import type * as Leaflet from 'leaflet';
import { LandingLocation } from '../landing-page.models';
import { RestService } from 'app/services/rest/rest.service';
import { LanguageService } from 'app/services/language/language.service';
import { loadLeaflet } from 'app/components/utils/load-leaflet';

@Component({
  selector: 'app-landing-locations',
  templateUrl: './landing-locations.component.html',
  styleUrls: ['./landing-locations.component.scss'],
  standalone: false,
})
export class LandingLocationsComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) data!: LandingLocation[];
  @ViewChild('mapContainer') private mapContainer!: ElementRef<HTMLDivElement>;

  selectedLocation: LandingLocation | null = null;
  mapLoading = true;

  private leaflet?: typeof Leaflet;
  private map?: Leaflet.Map;
  private markers = new Map<string, Leaflet.Marker>();
  private resizeObserver?: ResizeObserver;
  private hasUserSelection = false;
  private destroyed = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    public language: LanguageService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnChanges(): void {
    if (this.data) {
      this.selectedLocation = this.data.find((location) => location.id === this.selectedLocation?.id)
        || this.data[0]
        || null;
      this.updateMap();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const leaflet = await loadLeaflet();
    if (this.destroyed) {
      return;
    }

    this.leaflet = leaflet;
    this.map = leaflet.map(this.mapContainer.nativeElement, {
      scrollWheelZoom: false,
    }).setView([36.72, -4.425], 13);

    const tiles = leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    });
    tiles.on('loading', () => this.setMapLoading(true));
    tiles.on('load', () => this.setMapLoading(false));
    tiles.addTo(this.map);

    this.updateMap();
    this.resizeObserver = new ResizeObserver(() => {
      if (this.destroyed) {
        return;
      }
      this.map?.invalidateSize({ pan: false, debounceMoveend: true });
      if (!this.hasUserSelection) {
        this.fitAllLocations();
      }
    });
    this.resizeObserver.observe(this.mapContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.map?.remove();
  }

  selectLocation(location: LandingLocation): void {
    this.hasUserSelection = true;
    this.selectedLocation = location;
    this.updateMarkerIcons();

    if (this.map && this.markers.has(location.id)) {
      this.map.setView([location.latitude, location.longitude], Math.max(this.map.getZoom(), 15));
    }
  }

  private updateMap(): void {
    if (!this.map || !this.leaflet || !this.data) {
      return;
    }

    this.markers.forEach((marker) => marker.remove());
    this.markers.clear();

    for (const location of this.data) {
      if (!Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) {
        continue;
      }

      const coordinates: Leaflet.LatLngTuple = [location.latitude, location.longitude];
      const marker = this.leaflet.marker(coordinates, {
        icon: this.pinIcon(location.id === this.selectedLocation?.id),
        title: location.name,
        alt: location.name,
      }).addTo(this.map);

      const pinElement = marker.getElement();
      if (pinElement) {
        pinElement.dataset['locationId'] = location.id;
      }
      pinElement?.addEventListener('click', () => this.selectFromMarker(location));
      pinElement?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectFromMarker(location);
        }
      });
      this.markers.set(location.id, marker);
    }

    this.fitAllLocations();
  }

  private fitAllLocations(): void {
    if (!this.map || !this.leaflet || !this.markers.size) {
      return;
    }

    this.map.fitBounds(this.leaflet.latLngBounds(
      Array.from(this.markers.values(), (marker) => marker.getLatLng()),
    ), {
      paddingTopLeft: [40, 110],
      paddingBottomRight: [40, 40],
      maxZoom: 15,
    });
  }

  private updateMarkerIcons(): void {
    this.markers.forEach((marker, id) => {
      marker.getElement()?.querySelector('circle')?.setAttribute(
        'fill',
        id === this.selectedLocation?.id ? '#e23908' : '#ff5a26',
      );
    });
  }

  private selectFromMarker(location: LandingLocation): void {
    this.selectLocation(location);
    this.changeDetector.detectChanges();
  }

  private pinIcon(active: boolean): Leaflet.DivIcon {
    const color = active ? '#e23908' : '#ff5a26';
    return this.leaflet!.divIcon({
      className: 'landing-map-point',
     // html: `<svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true"><circle cx="15" cy="15" r="11" fill="${color}" stroke="white" stroke-width="3"/><circle cx="15" cy="15" r="4" fill="white"/></svg>`,
      html: `
      <svg width="60" height="60" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="${color}"/>
      </svg>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
  }

  private setMapLoading(loading: boolean): void {
    if (!this.destroyed && this.mapLoading !== loading) {
      this.mapLoading = loading;
      this.changeDetector.detectChanges();
    }
  }
}
